import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";
import Login from "./pages/Login";
import DevDiagnostics from "./pages/DevDiagnostics";
import UserManagement from "./pages/UserManagement";
import AuditLogViewer from "./pages/AuditLogViewer";
import DocumentTypesPage from "./pages/DocumentTypesPage";
import DocumentLibraryPage from "./pages/DocumentLibraryPage";
import EntityDocumentsPage from "./pages/EntityDocumentsPage";
import ClientListPage from "./pages/ClientListPage";
import ClientDetailPage from "./pages/ClientDetailPage";
import SchedulePage from "./pages/SchedulePage";
import VisitDetailPage from "./pages/VisitDetailPage";
import EVVInboxPage from "./pages/EVVInboxPage";
import EVVExceptionDetailPage from "./pages/EVVExceptionDetailPage";
import PersonnelListPage from "./pages/PersonnelListPage";
import PersonnelDetailPage from "./pages/PersonnelDetailPage";
import TrainingMatrixPage from "./pages/TrainingMatrixPage";
import SurveyDashboardPage from "./pages/SurveyDashboardPage";
import SandataImportPage from "./pages/SandataImportPage";
import SandataMappingsPage from "./pages/SandataMappingsPage";
import SystemSettingsPage from "./pages/SystemSettingsPage";
import KnowledgeBasePage from "./pages/KnowledgeBasePage";
import AuditDashboard from "./pages/AuditDashboard";
import AuditDetail from "./pages/AuditDetail";

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-slate-600">Loading...</div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// App Routes
const AppRoutes = () => {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route 
        path="/" 
        element={
          <ProtectedRoute>
            <DevDiagnostics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/diagnostics" 
        element={
          <ProtectedRoute>
            <DevDiagnostics />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/users" 
        element={
          <ProtectedRoute>
            <UserManagement />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audit-logs" 
        element={
          <ProtectedRoute>
            <AuditLogViewer />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/document-types" 
        element={
          <ProtectedRoute>
            <DocumentTypesPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/documents" 
        element={
          <ProtectedRoute>
            <DocumentLibraryPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/entity-documents" 
        element={
          <ProtectedRoute>
            <EntityDocumentsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clients" 
        element={
          <ProtectedRoute>
            <ClientListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/clients/:clientId" 
        element={
          <ProtectedRoute>
            <ClientDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/schedule" 
        element={
          <ProtectedRoute>
            <SchedulePage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/visits/:visitId" 
        element={
          <ProtectedRoute>
            <VisitDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/evv-exceptions" 
        element={
          <ProtectedRoute>
            <EVVInboxPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/evv-exceptions/:exceptionId" 
        element={
          <ProtectedRoute>
            <EVVExceptionDetailPage />
          </ProtectedRoute>
        } 
      />
      {/* Sprint 9: Sandata Import Pages */}
      <Route 
        path="/sandata-import" 
        element={
          <ProtectedRoute>
            <SandataImportPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/sandata-mappings" 
        element={
          <ProtectedRoute>
            <SandataMappingsPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/personnel" 
        element={
          <ProtectedRoute>
            <PersonnelListPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/personnel/:personnelId" 
        element={
          <ProtectedRoute>
            <PersonnelDetailPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/training-matrix" 
        element={
          <ProtectedRoute>
            <TrainingMatrixPage />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/survey-dashboard" 
        element={
          <ProtectedRoute>
            <SurveyDashboardPage />
          </ProtectedRoute>
        } 
      />
      {/* Sprint 13: System Settings - Admin/ComplianceManager only */}
      <Route 
        path="/system-settings" 
        element={
          <ProtectedRoute>
            <SystemSettingsPage />
          </ProtectedRoute>
        } 
      />
      {/* Knowledge Base - All authenticated users */}
      <Route 
        path="/knowledge-base" 
        element={
          <ProtectedRoute>
            <KnowledgeBasePage />
          </ProtectedRoute>
        } 
      />
      {/* Sprint 14: Audit Module */}
      <Route 
        path="/audits" 
        element={
          <ProtectedRoute>
            <AuditDashboard />
          </ProtectedRoute>
        } 
      />
      <Route 
        path="/audits/:id" 
        element={
          <ProtectedRoute>
            <AuditDetail />
          </ProtectedRoute>
        } 
      />
      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </AuthProvider>
  );
}

export default App;
